/**
 * BlindReview (VH-280)
 *
 * Two answers to the same case, today's model and a candidate, labelled only A and B.
 * The server decides the order and never says which is which, so the page cannot leak
 * it either; the pick goes back as A, B or "same" and the next pair comes with the reply.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../services/api'

export default function BlindReview({ run, onClose }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const key = ['model-trial-review', run.id]

  const { data: pair, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: async () => (await adminAPI.getModelTrialReview(run.id)).data.pair,
    staleTime: Infinity,
  })

  const pick = useMutation({
    mutationFn: async (choice) =>
      (await adminAPI.submitModelTrialReview(run.id, pair.answer_id, choice)).data.pair,
    onSuccess: (next) => {
      queryClient.setQueryData(key, next ?? null)
      queryClient.invalidateQueries({ queryKey: ['model-trials'] })
    },
  })

  return (
    <section className="rounded-lg border border-primary-200 p-4" data-testid="blind-review"
      aria-label={t('admin.modelTrials.reviewTitle', { feature: run.feature })}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900">
          {t('admin.modelTrials.reviewTitle', { feature: run.feature })}
        </h3>
        <button type="button" className="btn-outline text-sm" onClick={onClose}>{t('common.close')}</button>
      </div>
      <p className="text-xs text-gray-500 mt-1">{t('admin.modelTrials.reviewIntro')}</p>

      {isLoading && <p className="mt-3 text-sm text-gray-500">{t('common.loading')}</p>}
      {isError && <p className="mt-3 text-sm text-red-600" role="alert">{t('admin.modelTrials.errors.review')}</p>}
      {!isLoading && !isError && !pair && (
        <p className="mt-3 text-sm text-green-700" data-testid="review-done">{t('admin.modelTrials.reviewDone')}</p>
      )}

      {pair && (
        <>
          <p className="mt-3 text-xs text-gray-600">
            {t('admin.modelTrials.case', { ref: pair.case_ref })} · {t('admin.modelTrials.remaining', { count: pair.remaining })}
          </p>
          <div className="mt-2 grid gap-3 md:grid-cols-2">
            {[['A', pair.a], ['B', pair.b]].map(([label, text]) => (
              <div key={label} className="rounded border border-gray-200 p-3" data-testid={`answer-${label}`}>
                <p className="text-sm font-semibold text-gray-900">{t('admin.modelTrials.answer', { label })}</p>
                <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap break-words">
                  {text || <span className="italic text-gray-500">{t('admin.modelTrials.emptyAnswer')}</span>}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {[['A', 'pickA'], ['B', 'pickB'], ['same', 'pickSame']].map(([choice, labelKey]) => (
              <button key={choice} type="button" className="btn-outline text-sm" disabled={pick.isPending}
                onClick={() => pick.mutate(choice)} data-testid={`pick-${choice}`}>
                {t(`admin.modelTrials.${labelKey}`)}
              </button>
            ))}
          </div>
          {pick.isError && (
            <p className="mt-2 text-sm text-red-600" role="alert">
              {pick.error?.response?.data?.detail || t('admin.modelTrials.errors.pick')}
            </p>
          )}
        </>
      )}
    </section>
  )
}
